import { Link, NavLink } from "react-router-dom";
import type { Trip } from "@/api/types";
import { IconButton } from "./IconButton";
import "./trip-header.css";

export interface TripHeaderProps {
  trip: Trip;
  /**
   * Copy-the-trip-link share action behind the ⤴ button (US-04). Required:
   * the button always renders, so every screen mounting <TripHeader> must
   * wire it (typically via useShareTrip) — an optional prop silently
   * no-op'd the button on the tab screens. See useShareTrip.
   */
  onShare: () => void;
}

const TABS: ReadonlyArray<{ to: string; label: string; testId: string }> = [
  { to: "receipts", label: "Чеки", testId: "tab-receipts" },
  { to: "participants", label: "Учасники", testId: "tab-participants" },
  { to: "settle-up", label: "Розрахунок", testId: "tab-settlement" },
  { to: "settings", label: "Налаштування", testId: "tab-settings" },
];

export function TripHeader({ trip, onShare }: TripHeaderProps) {
  return (
    <div className="wp-trip-header">
      <div className="wp-trip-header__row">
        <Link to={`/t/${trip.trip_id}/receipts`} className="wp-trip-header__title-link">
          <h1 className="wp-trip-header__title" title={trip.name}>
            {trip.name}
          </h1>
          <p className="wp-trip-header__meta">
            <span
              className={`wp-trip-header__status wp-trip-header__status--${trip.status}`}
              data-testid="trip-status"
            >
              {trip.status === "open" ? "open" : "closed"}
            </span>
            <span aria-hidden="true">·</span>
            <span>{trip.settlement_ccy}</span>
          </p>
        </Link>
        <div className="wp-trip-header__actions">
          <IconButton aria-label="Поділитися посиланням на поїздку" onClick={onShare}>
            ⤴
          </IconButton>
          <Link
            to={`/t/${trip.trip_id}/settings`}
            className="wp-trip-header__settings"
            aria-label="Налаштування поїздки"
          >
            ⚙
          </Link>
        </div>
      </div>
      <nav className="wp-trip-header__tabs" aria-label="Розділи поїздки">
        <ul role="tablist">
          {TABS.map((t) => (
            <li key={t.to} role="presentation">
              <NavLink
                to={`/t/${trip.trip_id}/${t.to}`}
                role="tab"
                data-testid={t.testId}
                className={({ isActive }) =>
                  ["wp-trip-header__tab", isActive ? "wp-trip-header__tab--active" : ""]
                    .filter(Boolean)
                    .join(" ")
                }
              >
                {t.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}
